export type Auth0Profile = {
  sub: string;
  client_access?: {
    [clientId: string]: {
      clientName: string;
    };
  };
  name: string;
  userId: Guid;
  roles: string[];
  "/client_ids": Guid[];
  login_path: string;
  custom_login_path?: string;
};

// Permissions and roles should match backend roles/permissions
// However, it's not concrete yet
type Permission =
  | "bin-maintenance"
  | "order-batches"
  | "picking"
  | "goods-in"
  | "configure-workstation"
  | "cycle-count"
  | "settings:view";

type UserRole =
  | "admin"
  | "picker"
  | "receiver"
  | "inventory-manager"
  | "workstation-configurer"
  | "amazon-admin"
  | "amazon-picker"
  | "amazon-receiver"
  | "amazon-inventory-manager";

// Move to config eventually
const roleToPermissionMap: Record<UserRole, Permission[]> = {
  admin: [
    "picking",
    "goods-in",
    "configure-workstation",
    "order-batches",
    "bin-maintenance",
    "cycle-count",
    "settings:view"
  ],
  picker: ["picking", "configure-workstation", "cycle-count", "settings:view"],
  receiver: [
    "goods-in",
    "configure-workstation",
    "cycle-count",
    "settings:view"
  ],
  "inventory-manager": [
    "order-batches",
    "bin-maintenance",
    "cycle-count",
    "settings:view"
  ],
  "workstation-configurer": ["configure-workstation", "settings:view"],
  "amazon-admin": [
    "picking",
    "goods-in",
    "configure-workstation",
    "order-batches",
    "bin-maintenance",
    "cycle-count",
    "settings:view"
  ],
  "amazon-picker": ["picking", "configure-workstation"],
  "amazon-receiver": ["goods-in", "configure-workstation"],
  "amazon-inventory-manager": [
    "order-batches",
    "bin-maintenance",
    "cycle-count"
  ]
};

export const mapTokenRoleToDomainRole = (
  tokenRole: string
): UserRole | null => {
  switch (tokenRole) {
    case "admin":
    case "client-administrator":
      return "admin";
    case "powerpick-picker":
    case "picker":
      return "picker";
    case "powerpick-receiver":
      return "receiver";
    case "powerpick-inventory-manager":
      return "inventory-manager";
    case "qubit-workstation-configurer":
      return "workstation-configurer";
    case "amazon-admin":
      return "amazon-admin";
    case "amazon-picker":
      return "amazon-picker";
    case "amazon-receiver":
      return "amazon-receiver";
    case "amazon-inventory-manager":
      return "amazon-inventory-manager";
    default:
      return null;
  }
};

export const roleToPermissions = (role: UserRole): Permission[] => {
  return roleToPermissionMap[role] || [];
};

export const hasPermissions = (
  profile: Auth0Profile,
  permissions: Permission[]
): boolean => {
  const userRoles = profile.roles
    .map(mapTokenRoleToDomainRole)
    .filter((r) => r != null);
  const userPermissions = userRoles.flatMap(roleToPermissions);
  return permissions.every((permission) =>
    userPermissions.includes(permission)
  );
};

export function isClientAdmin(profile: Auth0Profile): boolean {
  return profile.roles.some(
    (role) => role === "client-administrator" || role === "amazon-admin"
  );
}

export function hasApiTokenManagementRole(profile: Auth0Profile): boolean {
  return profile.roles.some((role) => role === "access-admin");
}

export const canPick = (profile: Auth0Profile): boolean =>
  hasPermissions(profile, ["picking"]);

export const canDecant = (profile: Auth0Profile): boolean =>
  hasPermissions(profile, ["goods-in"]);

export const canConfigureWorkstation = (profile: Auth0Profile): boolean =>
  hasPermissions(profile, ["configure-workstation"]);

export const canCycleCount = (profile: Auth0Profile): boolean =>
  hasPermissions(profile, ["cycle-count"]);

export const canViewSettings = (profile: Auth0Profile): boolean =>
  hasPermissions(profile, ["settings:view"]);

export function getUserClientId(profile: Auth0Profile | null): string | null {
  if (
    profile &&
    profile["/client_ids"] &&
    Array.isArray(profile["/client_ids"]) &&
    profile["/client_ids"].length
  ) {
    return profile["/client_ids"][0];
  }

  return null;
}
